import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    BookOpen, Play, Clock, ChevronRight, CheckCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import Button from '../components/Button'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
    progresso: number
    total_aulas: number
    aulas_assistidas: number
}

export default function Cursos() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [cursos, setCursos] = useState<Curso[]>([])
    const [loading, setLoading] = useState(true)

    // Usar ref para controlar se já carregou uma vez, preservando entre rerenders
    const dadosJaCarregados = useRef(false)

    useEffect(() => {
        if (user && !dadosJaCarregados.current) {
            loadCursos()
            dadosJaCarregados.current = true
        }
    }, [user])

    // Preservar o estado ao mudar de aba
    useEffect(() => {
        const handleVisibilityChange = () => {
            // Quando a aba volta a ficar visível, não fazer nada especial
            // O estado React já preserva os dados
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    const loadCursos = async () => {
        try {
            setLoading(true)

            // Buscar cursos disponíveis para o usuário
            const { data: cursosData, error: cursosError } = await supabase
                .from('cursos')
                .select(`
                    id,
                    titulo,
                    descricao,
                    imagem_url,
                    duracao_estimada,
                    nivel,
                    status
                `)
                .eq('status', 'ativo')
                .order('ordem_exibicao')

            if (cursosError) throw cursosError

            // Para cada curso, calcular progresso e total de aulas
            const cursosComProgresso = await Promise.all(
                (cursosData || []).map(async (curso) => {
                    // Calcular progresso usando a função SQL
                    const { data: progressoData, error: progressoError } = await supabase
                        .rpc('calcular_progresso_curso', {
                            p_user_id: user!.id,
                            p_curso_id: curso.id
                        })

                    if (progressoError) {
                        console.error('Erro ao calcular progresso:', progressoError)
                    }

                    // Contar total de aulas
                    const { data: modulosData } = await supabase
                        .from('modulos')
                        .select('id')
                        .eq('curso_id', curso.id)

                    const moduloIds = modulosData?.map(m => m.id) || []

                    const { count: totalAulas } = await supabase
                        .from('aulas')
                        .select('*', { count: 'exact', head: true })
                        .in('modulo_id', moduloIds)

                    // Contar aulas assistidas
                    const { data: aulasData } = await supabase
                        .from('aulas')
                        .select('id, duracao')
                        .in('modulo_id', moduloIds)
                        .eq('status', 'ativo')

                    const aulaIds = aulasData?.map(a => a.id) || []

                    const { count: aulasAssistidas } = await supabase
                        .from('progresso_aulas')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user!.id)
                        .eq('assistida', true)
                        .in('aula_id', aulaIds)

                    // Calcular duração total somando todas as aulas do curso
                    let duracaoTotal = curso.duracao_estimada // Usar valor do banco como fallback
                    if (aulasData) {
                        // Somar todas as durações das aulas (em minutos)
                        duracaoTotal = aulasData.reduce((total, aula) => {
                            return total + (aula.duracao || 0)
                        }, 0)
                    }

                    return {
                        ...curso,
                        progresso: progressoData || 0,
                        total_aulas: totalAulas || 0,
                        aulas_assistidas: aulasAssistidas || 0,
                        duracao_estimada: duracaoTotal // Duração calculada da soma das aulas
                    }
                })
            )

            setCursos(cursosComProgresso)
        } catch (err) {
            console.error('Erro ao carregar cursos:', err)
        } finally {
            setLoading(false)
        }
    }


    const formatarDuracao = (minutos: number) => {
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const getNivelColor = (nivel: string) => {
        switch (nivel) {
            case 'iniciante':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'intermediario':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            case 'avancado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }


    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        Cursos Disponíveis
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Aprenda técnicas avançadas para impulsionar suas vendas
                    </p>
                </div>

                {/* Grid de Cursos */}
                {cursos.length === 0 ? (
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum curso disponível
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Novos cursos serão adicionados em breve.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cursos.map((curso) => (
                            <div
                                key={curso.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
                                onClick={() => navigate(`/cursos/${curso.id}`)}
                            >
                                {/* Imagem do Curso */}
                                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                                    {curso.imagem_url ? (
                                        <img
                                            src={curso.imagem_url}
                                            alt={curso.titulo}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <BookOpen className="h-16 w-16 text-white opacity-80" />
                                        </div>
                                    )}

                                    {/* Badge de Nível */}
                                    <div className="absolute top-3 left-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(curso.nivel)}`}>
                                            {curso.nivel}
                                        </span>
                                    </div>

                                    {/* Badge de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="absolute top-3 right-3">
                                            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                {curso.progresso}%
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Conteúdo do Card */}
                                <div className="p-6 flex flex-col flex-grow">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                                        {curso.titulo}
                                    </h3>

                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                        {curso.descricao || 'Descrição não disponível'}
                                    </p>

                                    {/* Informações do Curso */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{formatarDuracao(curso.duracao_estimada)}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Play className="h-4 w-4" />
                                            <span>{curso.aulas_assistidas}/{curso.total_aulas} aulas</span>
                                        </div>
                                    </div>

                                    {/* Barra de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="mb-4">
                                            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                <span>Progresso</span>
                                                <span>{curso.aulas_assistidas}/{curso.total_aulas} aulas</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${curso.progresso}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Espaçador para empurrar o botão para baixo */}
                                    <div className="flex-grow"></div>

                                    {/* Botão de Ação */}
                                    <div className="flex items-center justify-between mt-4">
                                        <Button
                                            variant={curso.aulas_assistidas > 0 ? 'btn_secondary' : 'btn_add'}
                                            onClick={() => navigate(`/cursos/${curso.id}`)}
                                            className="flex items-center gap-2"
                                        >
                                            {curso.aulas_assistidas > 0 ? 'Continuar' : 'Começar'}
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
